"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import Image from "next/image";

import { Dialog, DialogOverlay, DialogPortal } from "@/components/atoms/Dialog";
import { cn } from "@/lib/utils";

import {
  CloseCircleBoldIcon,
  DownloadArrowIcon,
  ExportLinearIcon,
} from "./icons";
import type {
  SpecialisationCarouselCard,
  SpecialisationCarouselModalTheme,
} from "./types";

interface SpecialisationCarouselModalProps {
  card: SpecialisationCarouselCard | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  theme: SpecialisationCarouselModalTheme;
  /** Resolved brochure URL for this card (global course brochure wins). */
  brochureLink: string;
  onPrimaryCtaClick: (card: SpecialisationCarouselCard) => void;
  onBrochureClick: (card: SpecialisationCarouselCard) => void;
}

/**
 * Card detail modal (Figma "Popup", 760px). Everything below is CMS-driven:
 * header tint, title colour, close glyph, body rich text (with its own heading
 * and body colours) and both footer CTAs.
 *
 * The body is authored in the CMS rich-text editor, so it is injected as HTML
 * and coloured through CSS custom properties rather than per-tag classes —
 * that way any structure the editor produces (h1–h6, p, ul, ol, a, strong…)
 * still picks up the configured palette.
 */
const SpecialisationCarouselModal = ({
  card,
  open,
  onOpenChange,
  theme,
  brochureLink,
  onPrimaryCtaClick,
  onBrochureClick,
}: SpecialisationCarouselModalProps) => {
  if (!card) return null;

  const title = card.meta_modal_title?.trim() || card.meta_title?.trim() || "";
  const body = card.meta_modal_body?.trim() || "";

  const primaryLabel =
    card.meta_modal_primary_cta_text?.trim() ||
    theme.primaryCtaText?.trim() ||
    "Apply Now";

  const brochureLabel =
    card.meta_modal_brochure_cta_text?.trim() ||
    theme.brochureCtaText?.trim() ||
    "Download Brochure";

  // Brochure CTA needs both an explicit opt-in and a link to download.
  const showBrochureCta =
    card.meta_show_modal_brochure_cta !== false &&
    card.meta_show_modal_brochure_cta !== 0 &&
    Boolean(brochureLink);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="z-[100] bg-black/70" />
        <div className="pointer-events-none fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <DialogPrimitive.Content
            className={cn(
              "pointer-events-auto flex max-h-[90dvh] w-full max-w-[min(100vw-2rem,760px)] flex-col overflow-hidden rounded-[20px] bg-white outline-none",
              "[&_*]:![font-family:Poppins,sans-serif]",
              "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            )}
            onOpenAutoFocus={(event) => event.preventDefault()}
          >
            <DialogPrimitive.Description className="sr-only">
              {title || "Programme details"}
            </DialogPrimitive.Description>

            {/* Header — tinted band, centred title, close glyph pinned right */}
            <div
              className="relative flex shrink-0 items-center gap-[10px] px-[20px] py-[20px] sm:px-[40px] sm:py-[30px]"
              style={{ background: theme.headerBackgroundColor }}
            >
              <DialogPrimitive.Title
                className="min-w-0 flex-1 text-center text-[20px] leading-[1.3] font-semibold tracking-[-0.2px] text-[#101112] sm:text-[28px] sm:tracking-[-0.28px]"
              >
                {title}
              </DialogPrimitive.Title>

              <DialogPrimitive.Close
                type="button"
                aria-label="Close"
                className="absolute top-[16px] right-[16px] flex size-[24px] shrink-0 items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-black/30"
              >
                {theme.closeIcon ? (
                  <Image
                    src={theme.closeIcon}
                    alt={theme.closeIconAlt || ""}
                    width={24}
                    height={24}
                    className="size-[24px] object-contain"
                  />
                ) : (
                  <CloseCircleBoldIcon
                    color={theme.closeIconColor}
                    className="size-[24px]"
                  />
                )}
              </DialogPrimitive.Close>
            </div>

            {/* Body — rich text authored in the CMS */}
            <div
              className="flex min-h-0 flex-1 flex-col items-center gap-[24px] overflow-y-auto px-[20px] pt-[20px] pb-[24px] sm:gap-[40px] sm:px-[40px] sm:pb-[40px]"
              style={{ background: theme.bodyBackgroundColor }}
            >
              {body ? (
                <div
                  className={cn(
                    // Colour is deliberately absent: every word here is authored in
                    // the rich-text editor, which writes its own colours inline. The
                    // widget only supplies structure (sizes, weights, rhythm) and the
                    // black it falls back to when the author has picked nothing.
                    "w-full text-[15px] leading-[1.55] tracking-[0.075px] text-[#101112]",
                    "[&_h1]:text-[22px] [&_h2]:text-[22px] [&_h3]:text-[22px] [&_h4]:text-[18px] [&_h5]:text-[16px] [&_h6]:text-[16px]",
                    "[&_h1]:leading-[1.4] [&_h2]:leading-[1.4] [&_h3]:leading-[1.4] [&_h4]:leading-[1.4]",
                    "[&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold [&_h4]:font-semibold [&_h5]:font-semibold [&_h6]:font-semibold",
                    "[&_h1]:tracking-[-0.11px] [&_h2]:tracking-[-0.11px] [&_h3]:tracking-[-0.11px]",
                    "[&_h1]:mb-[16px] [&_h2]:mb-[16px] [&_h3]:mb-[16px] [&_h4]:mb-[16px]",
                    "[&_h1:not(:first-child)]:mt-[40px] [&_h2:not(:first-child)]:mt-[40px] [&_h3:not(:first-child)]:mt-[40px]",
                    "[&_p]:mb-[12px] [&_p:last-child]:mb-0",
                    "[&_ul]:mb-[12px] [&_ul]:list-disc [&_ul]:pl-[20px]",
                    "[&_ol]:mb-[12px] [&_ol]:list-decimal [&_ol]:pl-[20px]",
                    "[&_li]:mb-[6px]",
                    "[&_a]:underline",
                    "[&_strong]:font-semibold",
                    "[&_img]:h-auto [&_img]:max-w-full",
                  )}
                  dangerouslySetInnerHTML={{ __html: body }}
                />
              ) : null}

              <div className="flex flex-col items-stretch justify-center gap-[16px] sm:flex-row sm:items-start sm:gap-[20px]">
                <button
                  type="button"
                  onClick={() => onPrimaryCtaClick(card)}
                  className="flex shrink-0 items-center justify-center gap-[10px] rounded-[8px] px-[24px] py-[16px] text-[16px] leading-[1.4] font-semibold whitespace-nowrap transition-transform hover:scale-[1.02]"
                  style={{
                    background: theme.primaryCtaBackgroundColor,
                    color: theme.primaryCtaTextColor,
                    filter: `drop-shadow(0px 10px 10px ${theme.ctaShadowColor})`,
                  }}
                >
                  {primaryLabel}
                  {theme.primaryCtaIcon ? (
                    <Image
                      src={theme.primaryCtaIcon}
                      alt={theme.primaryCtaIconAlt || ""}
                      width={24}
                      height={24}
                      className="size-[24px] shrink-0 object-contain"
                    />
                  ) : (
                    <ExportLinearIcon
                      color={theme.primaryCtaTextColor}
                      className="size-[24px] shrink-0"
                    />
                  )}
                </button>

                {showBrochureCta ? (
                  <button
                    type="button"
                    onClick={() => onBrochureClick(card)}
                    className="flex shrink-0 items-center justify-center gap-[10px] rounded-[8px] border border-solid px-[24px] py-[16px] text-[16px] leading-[1.4] font-semibold whitespace-nowrap transition-transform hover:scale-[1.02]"
                    style={{
                      background: theme.brochureCtaBackgroundColor,
                      color: theme.brochureCtaTextColor,
                      borderColor: theme.brochureCtaBorderColor,
                      boxShadow: `0px 10px 20px 0px ${theme.ctaShadowColor}`,
                    }}
                  >
                    {brochureLabel}
                    {theme.brochureCtaIcon ? (
                      <Image
                        src={theme.brochureCtaIcon}
                        alt={theme.brochureCtaIconAlt || ""}
                        width={24}
                        height={24}
                        className="size-[24px] shrink-0 object-contain"
                      />
                    ) : (
                      <DownloadArrowIcon
                        color={theme.brochureCtaTextColor}
                        className="size-[24px] shrink-0"
                      />
                    )}
                  </button>
                ) : null}
              </div>
            </div>
          </DialogPrimitive.Content>
        </div>
      </DialogPortal>
    </Dialog>
  );
};

export default SpecialisationCarouselModal;
